import { type UseQueryResult, useQuery } from '@tanstack/react-query';

import type { BitcoinData } from '@/app/overview/components/OverviewChart/utils';

import { get } from './api.helpers';
import type { Interval } from './api.types';
import { QueryKeys } from './query-keys';

export interface HistoryPriceResponse {
  history: BitcoinData[];
  change: string;
  source?: string;
}

export interface UseHistoryPriceParams {
  interval: Interval;
  enabled?: boolean;
}

// todo: switch to price oracle
// https://test-price.ducatprotocol.com/api/price/history?start=1748799868&res=1m

export const useHistoryPrice = ({ interval, enabled = true }: UseHistoryPriceParams): UseQueryResult<HistoryPriceResponse> => {
  // todo: first try a public open api, if that fails then fetch from our own.
  const path = `/api/price-history?timePeriod=${interval}`;

  return useQuery<HistoryPriceResponse>({
    enabled,
    queryKey: [QueryKeys.historyPrice, interval],
    queryFn: async ({ signal }) => {
      const result = await get<{ data: HistoryPriceResponse } | { error: string }>(path, undefined, { signal });
      if ('error' in result) throw new Error(result.error);
      if (result.data.source === 'fixture') throw new Error('Live price history is unavailable');
      return result.data;
    },
    // For 24h interval invalidate cache after 1 hour.
    staleTime: interval === '24h' ? 60 * 60 * 1000 : Infinity,
    retry: 2,
    retryDelay: 5000,
    // Recover an open chart after retries are exhausted; stop polling on success.
    refetchInterval: (query) => (query.state.status === 'error' ? 30_000 : false),
  });
};
